import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import classNames from 'classnames';

import Box from '../box/box.jsx';
import DOMElementRenderer from '../../containers/dom-element-renderer.jsx';
import Loupe from '../loupe/loupe.jsx';
import MonitorList from '../../containers/monitor-list.jsx';
import TargetHighlight from '../../containers/target-highlight.jsx';
import GreenFlagOverlay from '../../containers/green-flag-overlay.jsx';
import Question from '../../containers/question.jsx';
import MicIndicator from '../mic-indicator/mic-indicator.jsx';
import {STAGE_DISPLAY_SIZES} from '../../lib/layout-constants.js';
import {getStageDimensions, getMinWidth} from '../../lib/screen-utils.js';
import styles from './stage.css';

const StageComponent = props => {
    const {
        canvas,
        customStageSize,
        dragRef,
        isColorPicking,
        isFullScreen,
        isPlayerOnly,
        isStarted,
        isRtl,
        colorInfo,
        micIndicator,
        question,
        stageSize,
        stageZoom,
        useEditorDragStyle,
        onDeactivateColorPicker,
        onDoubleClick,
        onQuestionAnswered,
        ...boxProps
    } = props;

    // ===== 读取 localStorage 中的窗口模式设置 =====
    const [windowMode, setWindowMode] = useState(() => {
        return localStorage.getItem('stageWindowMode') === 'true';
    });

    // tw: 浮动窗口（作品控制页）的原生全屏状态
    const [isNativeFullscreen, setIsNativeFullscreen] = useState(false);

    // ===== 监听设置变化 =====
    useEffect(() => {
        const handler = (e) => {
            setWindowMode(e.detail.enabled);
        };
        window.addEventListener('stageWindowModeChange', handler);
        return () => window.removeEventListener('stageWindowModeChange', handler);
    }, []);

    // ===== 计算舞台尺寸（放在最前面，因为后续都会用到） =====
    // tw: 浮动窗口（作品控制页）原生全屏时，让舞台也跟着放大占满窗口
    const effectiveIsFullScreen = isFullScreen || isNativeFullscreen;
    let stageDimensions = getStageDimensions(stageSize, customStageSize, effectiveIsFullScreen, stageZoom);
    // tw: 当舞台元素处于原生全屏（F11 式）时，让舞台真正铺满整个屏幕，
    // 而不是沿用 CSS 全屏尺寸（会留出控制条边距）。
    if (isNativeFullscreen) {
        const aspect = customStageSize.width / customStageSize.height;
        let h = window.innerHeight;
        let w = h * aspect;
        if (w > window.innerWidth) {
            w = window.innerWidth;
            h = w / aspect;
        }
        stageDimensions = {
            ...stageDimensions,
            height: Math.round(h),
            width: Math.round(w),
            scale: w / stageDimensions.widthDefault
        };
    }
    const minWidth = getMinWidth(stageSize);
    const transformStyle = stageDimensions.width < minWidth && !effectiveIsFullScreen
        ? { transform: `translateX(${(minWidth - stageDimensions.width) / (isRtl ? -2 : 2)}px)` }
        : {};

    // ===== 原版舞台内容（不包裹浮动窗口） =====
    const originalStageContent = (
        <Box
            className={classNames(
                styles.stageWrapper,
                {[styles.withColorPicker]: !effectiveIsFullScreen && isColorPicking}
            )}
            onDoubleClick={onDoubleClick}
            style={isPlayerOnly ? null : { minWidth: `${minWidth + 2}px` }}
        >
            <Box
                className={classNames(styles.stage, { [styles.fullScreen]: effectiveIsFullScreen })}
                style={{
                    height: stageDimensions.height,
                    width: stageDimensions.width,
                    ...transformStyle
                }}
            >
                <DOMElementRenderer domElement={canvas} style={{ height: stageDimensions.height, width: stageDimensions.width }} {...boxProps} />
                <Box className={styles.customOverlays}>
                    <DOMElementRenderer domElement={props.overlay} />
                </Box>
                <Box className={styles.monitorWrapper}>
                    <MonitorList draggable={useEditorDragStyle} stageSize={stageDimensions} />
                </Box>
                <Box className={styles.frameWrapper}>
                    <TargetHighlight stageHeight={stageDimensions.height} stageWidth={stageDimensions.width} />
                </Box>
                {isColorPicking && colorInfo ? <Loupe colorInfo={colorInfo} /> : null}
            </Box>

            <Box
                className={classNames(styles.stageOverlays, { [styles.fullScreen]: effectiveIsFullScreen })}
                style={transformStyle}
            >
                <div
                    className={styles.stageBottomWrapper}
                    style={{ width: stageDimensions.width, height: stageDimensions.height }}
                >
                    {micIndicator ? <MicIndicator className={styles.micIndicator} stageSize={stageDimensions} /> : null}
                    {question === null ? null : (
                        <div className={styles.questionWrapper} style={{ width: stageDimensions.width }}>
                            <Question question={question} onQuestionAnswered={onQuestionAnswered} />
                        </div>
                    )}
                </div>
                <canvas className={styles.draggingSprite} height={0} ref={dragRef} width={0} />
            </Box>

            {isStarted ? null : (
                <GreenFlagOverlay
                    className={styles.greenFlagOverlay}
                    wrapperClass={styles.greenFlagOverlayWrapper}
                />
            )}
            {isColorPicking ? <Box className={styles.colorPickerBackground} onClick={onDeactivateColorPicker} /> : null}
        </Box>
    );

    // ============================================================
    // 浮动窗口模式的相关状态和逻辑（仅当 windowMode 为 true 时使用）
    // ============================================================
    const [isMinimized, setIsMinimized] = useState(false);
    const [windowPos, setWindowPos] = useState({ x: 100, y: 100 });
    const windowRef = useRef(null);
    const dragData = useRef({ isDragging: false, offsetX: 0, offsetY: 0 });
    const positionRef = useRef({ x: windowPos.x, y: windowPos.y });

    // FPS 相关
    const [fps, setFps] = useState(0);
    const [isFpsCollapsed, setIsFpsCollapsed] = useState(false);
    const [fpsPos, setFpsPos] = useState({ x: 20, y: 20 });
    const fpsRef = useRef(null);
    const fpsDragData = useRef({ isDragging: false, offsetX: 0, offsetY: 0 });
    const fpsTitleRef = useRef(null);
    const fpsBallRef = useRef(null);

    // FPS 测量
    useEffect(() => {
        let frameCount = 0;
        let lastTime = performance.now();
        let rafId = null;
        const measure = () => {
            const now = performance.now();
            frameCount++;
            if (now - lastTime >= 1000) {
                setFps(frameCount);
                frameCount = 0;
                lastTime = now;
            }
            rafId = requestAnimationFrame(measure);
        };
        measure();
        return () => cancelAnimationFrame(rafId);
    }, []);

    // 监听原生全屏变化
    useEffect(() => {
        const onFullscreenChange = () => {
            // tw: 仅当「浮动窗口本身」被原生全屏时才视为原生全屏，
            // 避免编辑器页整页全屏（F11 式）误触发此处导致舞台尺寸错乱。
            setIsNativeFullscreen(!!(windowRef.current && document.fullscreenElement === windowRef.current));
        };
        document.addEventListener('fullscreenchange', onFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
    }, []);

    // 舞台窗口拖拽
    useEffect(() => {
        const titleBar = document.querySelector('.stage-window-titlebar');
        if (!titleBar || !windowRef.current) return;

        const onDragStart = (e) => {
            if (e.type === 'mousedown' && e.button !== 0) return;
            const rect = windowRef.current.getBoundingClientRect();
            const clientX = e.clientX || e.touches?.[0]?.clientX;
            const clientY = e.clientY || e.touches?.[0]?.clientY;
            if (clientX == null) return;
            dragData.current = {
                isDragging: true,
                offsetX: clientX - rect.left,
                offsetY: clientY - rect.top,
            };
            positionRef.current = { x: windowPos.x, y: windowPos.y };
            e.preventDefault();

            const onMove = (ev) => {
                if (!dragData.current.isDragging) return;
                const cx = ev.clientX || ev.touches?.[0]?.clientX;
                const cy = ev.clientY || ev.touches?.[0]?.clientY;
                if (cx == null) return;
                let newX = cx - dragData.current.offsetX;
                let newY = cy - dragData.current.offsetY;
                const winWidth = windowRef.current.offsetWidth;
                const winHeight = windowRef.current.offsetHeight;
                newX = Math.max(0, Math.min(newX, window.innerWidth - winWidth));
                newY = Math.max(0, Math.min(newY, window.innerHeight - winHeight));
                if (windowRef.current) {
                    windowRef.current.style.left = newX + 'px';
                    windowRef.current.style.top = newY + 'px';
                }
                positionRef.current = { x: newX, y: newY };
            };

            const onUp = () => {
                dragData.current.isDragging = false;
                setWindowPos(positionRef.current);
                window.removeEventListener('mousemove', onMove);
                window.removeEventListener('mouseup', onUp);
                window.removeEventListener('touchmove', onMove);
                window.removeEventListener('touchend', onUp);
            };

            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onUp);
            window.addEventListener('touchmove', onMove, { passive: false });
            window.addEventListener('touchend', onUp);
        };

        titleBar.addEventListener('mousedown', onDragStart);
        titleBar.addEventListener('touchstart', onDragStart, { passive: false });

        return () => {
            titleBar.removeEventListener('mousedown', onDragStart);
            titleBar.removeEventListener('touchstart', onDragStart);
        };
    }, [windowPos]);

    // FPS 小窗口拖拽
    useEffect(() => {
        const target = isFpsCollapsed ? fpsBallRef.current : fpsTitleRef.current;
        if (!target || !fpsRef.current) return;

        const container = fpsRef.current.parentElement;
        if (!container) return;

        const onFpsDragStart = (e) => {
            if (isFpsCollapsed) return;
            e.preventDefault();
            const rect = fpsRef.current.getBoundingClientRect();
            const clientX = e.clientX || e.touches?.[0]?.clientX;
            const clientY = e.clientY || e.touches?.[0]?.clientY;
            if (clientX == null) return;
            fpsDragData.current = {
                isDragging: true,
                offsetX: clientX - rect.left,
                offsetY: clientY - rect.top,
            };

            const onMove = (ev) => {
                if (!fpsDragData.current.isDragging) return;
                const cx = ev.clientX || ev.touches?.[0]?.clientX;
                const cy = ev.clientY || ev.touches?.[0]?.clientY;
                if (cx == null) return;
                const containerRect = container.getBoundingClientRect();
                const winWidth = fpsRef.current.offsetWidth;
                const winHeight = fpsRef.current.offsetHeight;
                let newX = cx - containerRect.left - fpsDragData.current.offsetX;
                let newY = cy - containerRect.top - fpsDragData.current.offsetY;
                const maxX = Math.max(0, containerRect.width - winWidth);
                const maxY = Math.max(0, containerRect.height - winHeight);
                newX = Math.max(0, Math.min(newX, maxX));
                newY = Math.max(0, Math.min(newY, maxY));
                if (fpsRef.current) {
                    fpsRef.current.style.left = newX + 'px';
                    fpsRef.current.style.top = newY + 'px';
                }
                fpsDragData.current.lastPos = { x: newX, y: newY };
            };

            const onUp = () => {
                fpsDragData.current.isDragging = false;
                if (fpsDragData.current.lastPos) {
                    setFpsPos(fpsDragData.current.lastPos);
                }
                window.removeEventListener('mousemove', onMove);
                window.removeEventListener('mouseup', onUp);
                window.removeEventListener('touchmove', onMove);
                window.removeEventListener('touchend', onUp);
            };

            window.addEventListener('mousemove', onMove);
            window.addEventListener('mouseup', onUp);
            window.addEventListener('touchmove', onMove, { passive: false });
            window.addEventListener('touchend', onUp);
        };

        target.addEventListener('mousedown', onFpsDragStart);
        target.addEventListener('touchstart', onFpsDragStart, { passive: false });

        return () => {
            target.removeEventListener('mousedown', onFpsDragStart);
            target.removeEventListener('touchstart', onFpsDragStart);
        };
    }, [isFpsCollapsed]);

    const toggleMinimize = useCallback(() => setIsMinimized(prev => !prev), []);
    const toggleFpsCollapse = useCallback(() => setIsFpsCollapsed(prev => !prev), []);
    const toggleFullscreen = useCallback(async () => {
        if (!windowRef.current) return;
        try {
            if (document.fullscreenElement) {
                await document.exitFullscreen();
            } else {
                await windowRef.current.requestFullscreen();
            }
        } catch (err) {
            console.warn('全屏切换失败:', err);
        }
    }, []);

    // FPS 小窗口 JSX
    const fpsWindow = (
        <div
            ref={fpsRef}
            style={{
                position: 'absolute',
                left: fpsPos.x,
                top: fpsPos.y,
                zIndex: 100000,
                userSelect: 'none',
                fontFamily: 'Segoe UI, sans-serif',
                pointerEvents: 'auto',
                transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                transform: isFpsCollapsed ? 'scale(0.7)' : 'scale(1)',
                opacity: 1,
            }}
        >
            {isFpsCollapsed ? (
                <div
                    ref={fpsBallRef}
                    onClick={toggleFpsCollapse}
                    style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '50%',
                        background: 'rgba(30,30,30,0.85)',
                        backdropFilter: 'blur(4px)',
                        border: '2px solid rgba(0,255,0,0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#0f0',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        fontFamily: 'monospace',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.6)',
                        cursor: 'pointer',
                        pointerEvents: 'auto',
                    }}
                    title="展开 FPS"
                >
                    {fps}
                </div>
            ) : (
                <div
                    style={{
                        width: '160px',
                        backgroundColor: 'rgba(30,30,30,0.85)',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.6)',
                        backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(255,255,255,0.15)',
                        pointerEvents: 'auto',
                    }}
                >
                    <div
                        ref={fpsTitleRef}
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '8px 12px',
                            backgroundColor: 'rgba(0,0,0,0.3)',
                            borderRadius: '8px 8px 0 0',
                            cursor: 'grab',
                            color: '#ddd',
                            fontSize: '13px',
                            fontWeight: 600,
                            userSelect: 'none',
                            pointerEvents: 'auto',
                        }}
                    >
                        <span>📊 FPS</span>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsFpsCollapsed(true);
                            }}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#aaa',
                                fontSize: '18px',
                                cursor: 'pointer',
                                padding: '0 4px',
                                lineHeight: 1,
                                pointerEvents: 'auto',
                            }}
                            title="折叠"
                        >
                            ➖
                        </button>
                    </div>
                    <div
                        style={{
                            padding: '14px 10px 16px',
                            textAlign: 'center',
                            color: '#0f0',
                            fontFamily: 'monospace',
                            fontSize: '28px',
                            fontWeight: 'bold',
                            letterSpacing: '1px',
                            textShadow: '0 0 12px rgba(0,255,0,0.3)',
                        }}
                    >
                        {fps}
                        <span style={{ fontSize: '14px', color: '#888', marginLeft: '6px' }}>fps</span>
                    </div>
                </div>
            )}
        </div>
    );

    // 带 FPS 的舞台内容（用于浮动窗口）
    const stageContentWithFPS = (
        <>
            {originalStageContent}
            {fpsWindow}
        </>
    );

    // ===== 如果未启用窗口模式，直接返回原版内容 =====
    if (!windowMode) {
        return originalStageContent;
    }

    // ===== 浮动窗口模式 =====
    const isFullscreen = isNativeFullscreen;
    return (
        <div
            ref={windowRef}
            style={{
                position: 'fixed',
                left: isFullscreen ? 0 : windowPos.x,
                top: isFullscreen ? 0 : windowPos.y,
                zIndex: 10000,
                backgroundColor: '#2a2a2a',
                borderRadius: isFullscreen ? '0' : '8px',
                boxShadow: isFullscreen ? 'none' : '0 8px 32px rgba(0,0,0,0.6)',
                overflow: 'hidden',
                display: isFullscreen ? 'flex' : 'block',
                justifyContent: isFullscreen ? 'center' : 'flex-start',
                alignItems: isFullscreen ? 'center' : 'flex-start',
                height: isMinimized && !isFullscreen ? '36px' : isFullscreen ? '100vh' : 'auto',
                width: isMinimized && !isFullscreen ? 'auto' : isFullscreen ? '100vw' : stageDimensions.width + 2,
                minWidth: isMinimized && !isFullscreen ? '200px' : 'auto',
                pointerEvents: 'auto',
                border: isFullscreen ? 'none' : '1px solid rgba(255,255,255,0.1)',
                transition: isFullscreen ? 'none' : 'height 0.3s ease, width 0.3s ease, left 0.3s ease, top 0.3s ease',
            }}
        >
            {!isFullscreen && (
                <div
                    className="stage-window-titlebar"
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 12px',
                        height: '36px',
                        backgroundColor: 'rgba(40,40,40,0.9)',
                        borderBottom: isMinimized ? 'none' : '1px solid rgba(255,255,255,0.1)',
                        cursor: 'grab',
                        userSelect: 'none',
                        color: '#ddd',
                        fontSize: '14px',
                        fontWeight: 500,
                        fontFamily: 'Segoe UI, sans-serif',
                        flexShrink: 0,
                    }}
                >
                    <span>🖥️ 舞台</span>
                    <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                            onClick={toggleFullscreen}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#aaa',
                                fontSize: '18px',
                                cursor: 'pointer',
                                padding: '0 4px',
                                lineHeight: 1,
                            }}
                            title="全屏"
                        >
                            ⛶
                        </button>
                        <button
                            onClick={toggleMinimize}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#aaa',
                                fontSize: '18px',
                                cursor: 'pointer',
                                padding: '0 6px',
                                lineHeight: 1,
                            }}
                            title={isMinimized ? '展开舞台' : '最小化舞台'}
                        >
                            {isMinimized ? '➕' : '➖'}
                        </button>
                    </div>
                </div>
            )}

            <div
                style={{
                    display: isMinimized && !isFullscreen ? 'none' : 'block',
                    opacity: isMinimized && !isFullscreen ? 0 : 1,
                    maxHeight: isMinimized && !isFullscreen ? 0 : '10000px',
                    transition: isFullscreen ? 'none' : 'opacity 0.3s ease, max-height 0.3s ease',
                    overflow: 'hidden',
                }}
            >
                {stageContentWithFPS}
            </div>

            {isFullscreen && (
                <button
                    onClick={toggleFullscreen}
                    style={{
                        position: 'fixed',
                        top: '20px',
                        right: '20px',
                        zIndex: 100001,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '8px 12px',
                        fontSize: '18px',
                        cursor: 'pointer',
                        backdropFilter: 'blur(4px)',
                    }}
                    title="退出全屏"
                >
                    ⛶
                </button>
            )}
        </div>
    );
};

StageComponent.propTypes = {
    canvas: PropTypes.instanceOf(Element).isRequired,
    customStageSize: PropTypes.shape({ width: PropTypes.number, height: PropTypes.number }),
    overlay: PropTypes.instanceOf(Element).isRequired,
    colorInfo: Loupe.propTypes.colorInfo,
    dragRef: PropTypes.func,
    isColorPicking: PropTypes.bool,
    isFullScreen: PropTypes.bool.isRequired,
    isPlayerOnly: PropTypes.bool,
    isRtl: PropTypes.bool,
    isStarted: PropTypes.bool,
    micIndicator: PropTypes.bool,
    onDeactivateColorPicker: PropTypes.func,
    onDoubleClick: PropTypes.func,
    onQuestionAnswered: PropTypes.func,
    question: PropTypes.string,
    stageSize: PropTypes.oneOf(Object.keys(STAGE_DISPLAY_SIZES)).isRequired,
    stageZoom: PropTypes.number,
    useEditorDragStyle: PropTypes.bool,
};
StageComponent.defaultProps = {
    dragRef: () => {},
};

export default StageComponent;
